// PasskeyEnrollPrompt — one-time post-login nudge (mobile) to turn on biometric
// unlock, so the user stops copying codes from their authenticator app on every
// visit. Enabling registers a local device biometric; sign-in itself is still
// Cognito password + TOTP.
import React, { useState, useEffect, useRef } from 'react';
import { useFileContext } from '../../ContextAPI/FileContext';
import { isAppLockSupported, isAppLockEnabled, enableAppLock } from '../../utils/appLock';
import { isMobileDevice } from '../../utils/device';
import FingerprintIcon from './FingerprintIcon';
import './PasskeyEnrollPrompt.css';

const dismissKey = (email) => `appLockDismissed:${email || 'unknown'}`;

const PasskeyEnrollPrompt = () => {
  const { auth, userEmail } = useFileContext();
  const [visible, setVisible] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);
  const shownRef = useRef(false);

  useEffect(() => {
    if (shownRef.current || done) return;
    if (!auth) return;
    if (!isMobileDevice() || !isAppLockSupported()) return;
    if (isAppLockEnabled()) return; // already on
    let dismissed = false;
    try { dismissed = !!localStorage.getItem(dismissKey(userEmail)); } catch (e) { /* ignore */ }
    if (dismissed) return;
    shownRef.current = true;
    setVisible(true);
  }, [auth, userEmail, done]);

  useEffect(() => {
    if (!auth) { setVisible(false); setDone(false); shownRef.current = false; }
  }, [auth]);

  const handleEnable = async () => {
    setError('');
    setBusy(true);
    try {
      await enableAppLock(userEmail);
      setDone(true);
      setTimeout(() => { setVisible(false); setDone(false); }, 1800);
    } catch (err) {
      console.warn('App lock enable failed:', err);
      const name = err?.name || 'Error';
      if (name === 'NotAllowedError' || /cancel|timed out|not allowed/i.test(err?.message || '')) {
        setError('Cancelled. You can turn this on later from Account.');
      } else {
        setError(`Couldn’t enable it. [${name}: ${err?.message || 'unknown'}]`);
      }
    } finally {
      setBusy(false);
    }
  };

  const handleNotNow = () => {
    try { localStorage.setItem(dismissKey(userEmail), String(Date.now())); } catch (e) { /* ignore */ }
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div className="passkey-prompt" role="dialog" aria-label="Enable biometric unlock">
      <div className="passkey-prompt__icon" aria-hidden="true">
        <FingerprintIcon />
      </div>

      {done ? (
        <div className="passkey-prompt__body">
          <p className="passkey-prompt__title">Biometric unlock is on ✓</p>
          <p className="passkey-prompt__subtitle">Next time, just touch the sensor to get in.</p>
        </div>
      ) : (
        <>
          <div className="passkey-prompt__body">
            <p className="passkey-prompt__title">Skip the authenticator app</p>
            <p className="passkey-prompt__subtitle">
              Unlock with your fingerprint or face on this device instead of copying a code every
              time you come back.
            </p>
            {error && <p className="passkey-prompt__error">{error}</p>}
          </div>
          <div className="passkey-prompt__actions">
            <button type="button" className="passkey-prompt__enable" onClick={handleEnable} disabled={busy}>
              {busy ? 'Waiting for biometric…' : 'Enable biometric unlock'}
            </button>
            <button type="button" className="passkey-prompt__later" onClick={handleNotNow} disabled={busy}>
              Not now
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default PasskeyEnrollPrompt;
